<template>
	<slot :loading="loading" :onUpdate="onUpdate" :onReset="onReset" :onDecrement="decrement" :onIncrement="increment" :status="status" :quantity="quantity" />
</template>

<script setup>
	const model = defineModel();
	const webshop = useWebshop();

	let gtm;
	if (__GTM_TRACKING__) gtm = useGtm();

	const config = useAppConfig();
	const props = defineProps({
		item: Object,
		quantity: {
			type: [Number, String],
			default: 1,
		},
		limit: {
			type: [Number, String],
			default: 0,
		},
		mode: String,
	});
	const emit = defineEmits(['update']);

	const quantity = ref(props.quantity);
	const loading = ref(false);
	const status = ref('');
	const {events} = inject('baseWebshopCartData', {events: null});
	model.value = props.quantity;

	let typingTimer;
	let prevQuantity = props.quantity;

	// run updateQty after 300ms of no typing
	async function onUpdate(event) {
		const inputValue = event.target.value;
		if (inputValue === '') {
			return;
		}
		const cleanedQuantity = cleanAndParseNumber(inputValue);
		quantity.value = cleanedQuantity;
		event.target.value = cleanedQuantity;
		checkLimit(cleanedQuantity);
		if (props.mode == 'cart' && props.item) {
			clearTimeout(typingTimer);
			typingTimer = setTimeout(updateQty, 300);
		}
		model.value = quantity.value;
		emit('update', quantity.value);
	}

	function onReset(event) {
		if (event.target.value === '') {
			event.target.value = quantity.value;
			quantity.value = quantity.value;
			model.value = quantity.value;
			emit('update', quantity.value);
		}
	}

	function checkLimit(value) {
		if (value <= 0) quantity.value = 0;

		if (props.mode == 'cart') return;
		if (props.limit && value > +props.limit) quantity.value = +props.limit;
		if (value <= +props.quantity) quantity.value = +props.quantity;
		emit('update', quantity.value);
		model.value = quantity.value;
	}

	// watch if quantity prop has changed and update related values
	watch(
		() => props.quantity,
		async () => {
			quantity.value = props.quantity;
			prevQuantity = props.quantity;
			model.value = props.quantity;
		}
	);

	// update quantity limit when switching between variations
	watch(
		() => props.limit,
		() => checkLimit(quantity.value)
	);

	// clean input from non-numeric characters
	function cleanAndParseNumber(input) {
		const cleanedInput = input
			.toString()
			.replace(/^-|[^0-9]+/g, '')
			.replace(/^0+/, '');
		return parseInt(cleanedInput, 10) || 1;
	}

	async function updateQty() {
		// update only if quantity has changed
		if (quantity.value == prevQuantity) return;

		loading.value = true;

		// update quantity
		const res = await webshop.updateProduct([
			{
				shopping_cart_code: props.item.shopping_cart_code,
				quantity: quantity.value,
			},
		]);

		if (res?.productResponse?.data?.labels_name || res?.productResponse?.data?.label_name) {
			// If qty reached the limit, reset to max available quantity. Multiple conditions to cover different HAPI responses
			if (res.productResponse?.data?.labels_name?.[props.item.shopping_cart_code] == 'error_limitqty') {
				quantity.value = res.productResponse.data.available_qty;
			}
			if (res.productResponse?.data?.label_name == 'error_product_maximum_qty') {
				quantity.value = res.productResponse.data.maximum_qty;
			}

			// Show status message for 4 seconds
			status.value = res?.productResponse?.data?.labels_name?.[props.item.shopping_cart_code] || res?.productResponse?.data?.label_name;
			setTimeout(() => (status.value = ''), 4000);
		}

		// gtm tracking
		if (res?.productResponse?.success && gtm) {
			let qty = res.productResponse?.data?.new_qty;
			if (qty < 0) {
				gtm.gtmTrack('removeProduct', {items: props.item, quantity: qty * -1});
			} else {
				gtm.gtmTrack('addProduct', {items: props.item, quantity: qty});
			}
		}

		events.value = {event: 'updateProduct', quantity: quantity.value, prevQuantity: prevQuantity, product: props.item, ...res};
		loading.value = false;
		prevQuantity = quantity.value;
	}

	async function increment() {
		quantity.value++;
		checkLimit(quantity.value);
		if (props.mode == 'cart' && props.item) {
			clearTimeout(typingTimer);
			typingTimer = setTimeout(updateQty, 500);
			return;
		}
		emit('update', quantity.value);
		model.value = quantity.value;
		return quantity.value;
	}

	async function decrement() {
		quantity.value--;
		checkLimit(quantity.value);
		if (props.mode == 'cart' && props.item) {
			clearTimeout(typingTimer);
			typingTimer = setTimeout(updateQty, 500);
			return;
		}
		emit('update', quantity.value);
		model.value = quantity.value;
		return quantity.value;
	}
</script>
